import { MarketplaceCampaign } from "@redotech/redo-marketplace-common/campaigns";
import { createEvent, DateArray, EventAttributes } from "ics";

export interface CalendarEventData {
  campaign: MarketplaceCampaign;
}

/**
 * Converts a Date to the DateArray format required by the ics library
 * DateArray format: [year, month, day, hour, minute, second]
 * This converts to UTC since we'll specify timezone separately in the event
 */
function dateToDateArray(date: Date): DateArray {
  // ics DateArray format: [year, month, day, hour, minute] in UTC
  return [
    date.getUTCFullYear(),
    date.getUTCMonth() + 1, // ics expects 1-12, JS Date gives 0-11
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
  ];
}

/**
 * Generates a calendar invite (ICS file) for a marketplace campaign start time
 */
export function generateCampaignCalendarInvite(
  data: CalendarEventData,
): Promise<string> {
  const { campaign } = data;

  const startDate = new Date(campaign.startTime);
  // Create a 1-hour event starting at campaign start time
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

  const eventAttributes: EventAttributes = {
    title: `${campaign.name} - Starts`,
    description: campaign.description
      ? `${campaign.description}\n\nThis is a reminder that the exclusive marketplace campaign starts now. Access the event at https://marketplace.getredo.com/${campaign.slug}`
      : "Reminder: Exclusive marketplace event starts now!",
    start: dateToDateArray(startDate),
    end: dateToDateArray(endDate),
    startInputType: "utc",
    endInputType: "utc",
    status: "CONFIRMED",
    busyStatus: "FREE",
    organizer: { name: "Redo Marketplace", email: "no-reply@redo.com" },
    uid: `campaign-${campaign.id}@redo.com`,
  };

  return new Promise((resolve, reject) => {
    createEvent(eventAttributes, (error, value) => {
      if (error) {
        reject(error);
      } else {
        resolve(value);
      }
    });
  });
}

/**
 * Downloads a calendar invite file to the user's device
 */
export async function downloadCalendarInvite(
  data: CalendarEventData,
): Promise<void> {
  try {
    const icsContent = await generateCampaignCalendarInvite(data);
    const filename = `${data.campaign.name.replace(/[^a-z0-9]/gi, "_")}_Event.ics`;

    // Create a blob with the ICS content
    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    // Create a temporary download link
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";

    // Trigger the download
    document.body.append(anchor);
    anchor.click();
    anchor.remove();

    // Clean up the object URL
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading calendar invite:", error);
    throw error;
  }
}
